import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LinkText } from "../../components/Core/AppText/AppText";
import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import VirtualizedTableLayout from "../../components/Layout/VirtualizedTableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Tab from "../../components/Shared/Tabs/Tab";
import { CustomerUsersListBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import useQueryState from "../../hooks/useQueryState";
import { useCustomerUsersInfinite } from "../../networking/customer.hooks";
import { cn } from "../../utils/cn";
import { formatRoute } from "../../utils/route.utils";

const columnWidths = {
	firstName: "15%",
	lastName: "15%",
	title: "14%",
	email: "22%",
	phone: "14%",
	customers: "20%",
};

type ActiveFilter = "active" | "inactive" | "all";

const ACTIVE_FILTER_OPTIONS: { value: ActiveFilter; label: string }[] = [
	{ value: "active", label: "CUSTOMER_USERS_FILTER_ACTIVE" },
	{ value: "inactive", label: "CUSTOMER_USERS_FILTER_INACTIVE" },
	{ value: "all", label: "CUSTOMER_USERS_FILTER_ALL" },
];

/**
 * Lists all customer accounts (kundanvändare) belonging to the authed
 * organisation — reached from the customers list's "..." overflow menu
 * ("Lista kundanvändare"). Backed by GET /customer-account, which scopes the
 * result to the organisation (and resolves each user's customer names + phone).
 *
 * Search and the status pills are applied server-side (`keyword` + `active`)
 * and the list is paged with infinite scroll. Large organisations have more
 * customer users than one page holds, so filtering client-side over a single
 * page silently dropped matches (a search for "joel" only found users whose
 * first name sorted into the first page). Rows open the user's edit form (via
 * their first linked customer, since the form is customer-scoped).
 */
const CustomerUsersListPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useQueryState<string>("search", { initialValue: "" });
	const [activeFilter, setActiveFilter] = useQueryState<ActiveFilter>("status", {
		initialValue: "active",
	});
	const keyword = useDebounce(searchTerm, 250);

	const {
		customerUsers: rows,
		isLoading,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useCustomerUsersInfinite({
		sort: "firstName",
		pageSize: 200,
		keyword: (keyword || "").trim() || undefined,
		active: activeFilter === "all" ? undefined : activeFilter === "active",
	});

	const onEndReached = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const navigateToUser = (index: number) => {
		const user = rows[index];
		const customerId = user?.profile?.customers?.[0]?.id;
		if (!user || !customerId) return;
		navigate(formatRoute(routes.CUSTOMER_USER_EDIT, [customerId, user.id]));
	};

	const renderTableHeader = useCallback(
		() => (
			<thead className="border-b-2 border-border">
				<tr className="whitespace-nowrap bg-muted text-left text-sm font-medium">
					<th className="px-4 pb-2 pt-3 pl-6" style={{ width: columnWidths.firstName }}>
						{t("FIRST_NAME")}
					</th>
					<th className="px-4 pb-2 pt-3" style={{ width: columnWidths.lastName }}>
						{t("LAST_NAME")}
					</th>
					<th className="px-4 pb-2 pt-3" style={{ width: columnWidths.title }}>
						{t("TITLE")}
					</th>
					<th className="px-4 pb-2 pt-3" style={{ width: columnWidths.email }}>
						{t("EMAIL")}
					</th>
					<th className="px-4 pb-2 pt-3" style={{ width: columnWidths.phone }}>
						{t("PHONE")}
					</th>
					<th className="px-4 pb-2 pt-3" style={{ width: columnWidths.customers }}>
						{t("CUSTOMER_USERS_CUSTOMERS")}
					</th>
				</tr>
			</thead>
		),
		[t]
	);

	const renderTableRow = useCallback(
		(index: number) => {
			const user = rows[index];
			if (!user) return null;

			const baseCellClass =
				"px-4 py-2 border-t border-border border-solid align-middle text-base truncate";

			const customerNames = (user.profile?.customers || [])
				.map((c) => c.name)
				.filter(Boolean)
				.join(", ");

			return (
				<>
					<td className={cn(baseCellClass, "pl-6")} style={{ width: columnWidths.firstName }}>
						{user.firstName || "-"}
					</td>
					<td className={baseCellClass} style={{ width: columnWidths.lastName }}>
						{user.lastName || "-"}
					</td>
					<td className={baseCellClass} style={{ width: columnWidths.title }}>
						{user.profile?.title || "-"}
					</td>
					<td className={baseCellClass} style={{ width: columnWidths.email }}>
						{user.email || "-"}
					</td>
					<td
						className={baseCellClass}
						style={{ width: columnWidths.phone }}
						onClick={(e) => e.stopPropagation()}
					>
						{user.profile?.phoneNumber ? (
							<a href={`tel:${user.profile.phoneNumber}`}>
								<LinkText as="span">{user.profile.phoneNumber}</LinkText>
							</a>
						) : (
							"-"
						)}
					</td>
					<td
						className={baseCellClass}
						style={{ width: columnWidths.customers }}
						title={customerNames || undefined}
					>
						{customerNames || "-"}
					</td>
				</>
			);
		},
		[rows]
	);

	return (
		<>
			<Breadcrumbs items={CustomerUsersListBreadcrumbs} />

			<div className="px-4 pt-4">
				<div className="mb-3 flex items-center gap-5">
					<div className="w-[240px] shrink-0">
						<AppInputNew
							containerClassName="[&>p:last-child]:hidden"
							placeholder={t("SEARCH")}
							icon="search"
							value={searchTerm || ""}
							onChange={(e: any) => setSearchTerm(e.target.value)}
						/>
					</div>

					<div className="flex flex-wrap items-center gap-2">
						{ACTIVE_FILTER_OPTIONS.map((option) => (
							<Tab
								key={option.value}
								role="filter"
								text={option.label}
								active={activeFilter === option.value}
								action={() => setActiveFilter(option.value)}
							/>
						))}
					</div>
				</div>
			</div>

			{isLoading ? (
				<LoadingIndicator className="mt-4" />
			) : rows.length > 0 ? (
				<VirtualizedTableLayout
					itemCount={rows.length}
					estimatedRowHeight={48}
					overscan={10}
					renderHeader={renderTableHeader}
					renderRow={renderTableRow}
					getRowProps={(index) =>
						({ "data-contact": rows[index]?.id } as React.HTMLAttributes<HTMLTableRowElement>)
					}
					onRowClick={navigateToUser}
					onEndReached={onEndReached}
					isFetchingNextPage={isFetchingNextPage}
					containerHeight="calc(100vh - 100px)"
					selectable
				/>
			) : (
				<EmptyState margin="2rem 0" label="customers.user.noUsers" />
			)}
		</>
	);
};

export default CustomerUsersListPage;
